/*
 * lineup-matrix.js — ラインナップ比較チャート（sections/main-lineup-matrix.liquid）の最小 JS
 * 正本は docs/lineup_matrix.md。
 *
 * 担当は2つだけ（チャートはサーバー描画＝JS 無効でも成立する progressive enhancement）:
 * 1. タブ切替: role=tablist の aria 状態管理・矢印キー移動・URL hash（#road / #trail / #other）との同期
 *    （直リンクの初期タブ選択と、切替時の history.replaceState。要素 id は hash と一致させない＝ネイティブの
 *    アンカージャンプでヘッダー下へ飛ばないように）。
 * 2. カード: マーカーの <template data-lineup-card-for> をパネル共通のカードホストへ clone して表示。
 *    PC（hover 環境）は pointerenter で仮表示・click で固定（pinned）・マーカー近傍にフロート配置。
 *    SP（749px 以下）は画面下部の非モーダルパネル（data-layout=fixed）＝背面操作可・別マーカーのタップで乗り換え。
 *    body のスクロールロックは行わない（touch_scroll_lock の3点セット非該当・モーダル化するなら3点セットを踏む）。
 * PDP 仕様欄直下の導線（docs/lineup_matrix.md「導線設計」）からの着地も hash（#road / #trail）のタブ選択だけ＝
 *    元商品のカードを開かない・スクロールしない（オーナー確定 2026-09-01・一覧性を損なうため）。
 * テーマエディタ: shopify:block:select で該当タブへ切替＋カードを固定表示（編集中の点を目視できる）。
 */

(() => {
  if (customElements.get('lineup-matrix')) return;

  const FIXED_LAYOUT_MQ = window.matchMedia('(max-width: 749px)');
  const CLOSE_DELAY_MS = 180;

  class LineupMatrix extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onHashChange = this.onHashChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-lineup-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-lineup-panel]'));
      this.markers = Array.from(this.querySelectorAll('[data-lineup-marker]'));
      this.activeMarker = null;
      this.activeCard = null;
      this.pinned = false;
      this.closeTimer = null;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.lineupTab, { updateHash: true }));
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
      });
      const initialTab = this.tabFromHash() || (this.tabs[0] && this.tabs[0].dataset.lineupTab);
      if (initialTab) this.selectTab(initialTab, { updateHash: false });

      this.markers.forEach((marker) => {
        marker.addEventListener('click', () => this.onMarkerClick(marker));
        marker.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.cancelClose();
          if (!this.pinned) this.openCard(marker, { pinned: false });
        });
        marker.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.scheduleClose();
        });
        marker.addEventListener('focus', () => {
          if (!this.pinned) this.openCard(marker, { pinned: false });
        });
      });

      this.querySelectorAll('[data-lineup-card]').forEach((card) => {
        card.addEventListener('pointerenter', () => this.cancelClose());
        card.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.scheduleClose();
        });
        const close = card.querySelector('[data-lineup-card-close]');
        if (close) close.addEventListener('click', () => this.closeCard());
      });

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('hashchange', this.onHashChange);
      window.addEventListener('resize', this.onResize);
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('hashchange', this.onHashChange);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.cancelClose();
    }

    /* ── タブ ─────────────────────────────── */

    tabFromHash() {
      const key = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      if (!key) return null;
      return this.tabs.some((tab) => tab.dataset.lineupTab === key) ? key : null;
    }

    selectTab(key, { updateHash }) {
      if (!this.tabs.some((tab) => tab.dataset.lineupTab === key)) return;
      this.closeCard();
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.lineupTab === key;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.lineupPanel !== key;
      });
      if (updateHash && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#${key}`);
      }
    }

    onTabKeydown(event, tab) {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = this.tabs.indexOf(tab);
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      this.tabs[next].focus();
      this.selectTab(this.tabs[next].dataset.lineupTab, { updateHash: true });
    }

    onHashChange() {
      const key = this.tabFromHash();
      if (key) this.selectTab(key, { updateHash: false });
    }

    /* ── カード ─────────────────────────────── */

    onMarkerClick(marker) {
      if (this.activeMarker === marker && this.pinned) {
        this.closeCard();
        return;
      }
      this.openCard(marker, { pinned: true });
    }

    openCard(marker, { pinned }) {
      const panel = marker.closest('[data-lineup-panel]');
      if (!panel) return;
      const card = panel.querySelector('[data-lineup-card]');
      const body = card && card.querySelector('[data-lineup-card-body]');
      const template = panel.querySelector(`template[data-lineup-card-for="${marker.dataset.blockId}"]`);
      if (!card || !body || !template) return;

      if (this.activeMarker !== marker) {
        body.replaceChildren(template.content.cloneNode(true));
      }
      if (this.activeMarker && this.activeMarker !== marker) this.setMarkerState(this.activeMarker, false);
      this.activeMarker = marker;
      this.activeCard = card;
      this.pinned = pinned;
      this.setMarkerState(marker, true);
      card.hidden = false;
      this.positionCard();
    }

    closeCard() {
      this.cancelClose();
      if (this.activeMarker) this.setMarkerState(this.activeMarker, false);
      if (this.activeCard) {
        this.activeCard.hidden = true;
        this.activeCard.removeAttribute('style');
      }
      this.activeMarker = null;
      this.activeCard = null;
      this.pinned = false;
    }

    setMarkerState(marker, active) {
      marker.classList.toggle('is-active', active);
      marker.setAttribute('aria-expanded', active ? 'true' : 'false');
    }

    scheduleClose() {
      if (this.pinned) return;
      this.cancelClose();
      this.closeTimer = window.setTimeout(() => this.closeCard(), CLOSE_DELAY_MS);
    }

    cancelClose() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    positionCard() {
      const card = this.activeCard;
      const marker = this.activeMarker;
      if (!card || !marker) return;

      if (FIXED_LAYOUT_MQ.matches) {
        card.dataset.layout = 'fixed';
        card.removeAttribute('style');
        return;
      }
      card.dataset.layout = 'float';

      const panel = card.offsetParent || card.parentElement;
      const panelRect = panel.getBoundingClientRect();
      const markerRect = marker.getBoundingClientRect();
      const gap = 12;
      const cardWidth = card.offsetWidth;
      const cardHeight = card.offsetHeight;

      let left = markerRect.right - panelRect.left + gap;
      if (left + cardWidth > panelRect.width) left = markerRect.left - panelRect.left - gap - cardWidth;
      if (left < 0) left = Math.max(0, Math.min(panelRect.width - cardWidth, markerRect.left - panelRect.left));

      let top = markerRect.top - panelRect.top + markerRect.height / 2 - cardHeight / 2;
      top = Math.max(0, Math.min(panelRect.height - cardHeight, top));

      card.style.left = `${Math.round(left)}px`;
      card.style.top = `${Math.round(top)}px`;
    }

    onResize() {
      if (this.activeCard) this.positionCard();
    }

    onDocumentClick(event) {
      if (!this.activeCard) return;
      const target = event.target;
      if (this.activeCard.contains(target)) return;
      if (this.activeMarker && this.activeMarker.contains(target)) return;
      if (target.closest && target.closest('[data-lineup-marker]')) return;
      this.closeCard();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.activeCard) {
        const marker = this.activeMarker;
        this.closeCard();
        if (marker) marker.focus();
      }
    }

    /* ── テーマエディタ ────────────────────────── */

    onBlockSelect(event) {
      const detail = event.detail || {};
      if (detail.sectionId && detail.sectionId !== this.dataset.sectionId) return;
      const marker = this.markers.find((candidate) => candidate.dataset.blockId === detail.blockId);
      if (!marker) return;
      const panel = marker.closest('[data-lineup-panel]');
      if (panel) this.selectTab(panel.dataset.lineupPanel, { updateHash: false });
      this.openCard(marker, { pinned: true });
    }
  }

  customElements.define('lineup-matrix', LineupMatrix);
})();
